import {
  Box,
  Card,
  Grid,
  Text,
  Badge,
  Table,
  Stack,
  Divider,
  SegmentedControl,
  Group,
  Flex,
  Title,
} from "@mantine/core";
import { useMemo, useState } from "react";
import type { ReadingAssessmentTestResult } from "types/testTypes";
import type { ReadingAssessmentDerivedMetrics, ReadingFixationRecord } from "types/tests";
import ReadingLayoutRenderer from "@components/common/ReadingLayoutRenderer";
import { useReadingLayoutPages } from "@hooks/useReadingLayoutPages";
import { Printable, usePrintableContext } from "@components/Printable";
import { useReadingAssessmentRisk } from "./hooks/useReadingAssessmentRisk";
import {
  ReadingAssessmentDashboard,
  ReadingAssessmentScoreSummary,
} from "./sections/ReadingAssessmentDashboard";
import { aggregateGazeTimePerWord, gazeTimeToColorMap } from "./utils/WordHeatmapUtils";
import { IconSettings } from "@tabler/icons-react";
import "./sections/ReadingAssessmentDashboard.scss";

interface ReadingAssessmentTestEntryProps {
  entry: ReadingAssessmentTestResult | undefined;
  header?: React.ReactNode;
}

function ReadingAssessmentTestEntry({ entry, header }: ReadingAssessmentTestEntryProps) {
  const testResults = entry as ReadingAssessmentTestResult | undefined;
  const results = testResults?.results;
  const layout = useReadingLayoutPages({
    enabled: Boolean(results && results.schemaVersion === 2),
    hash: results?.hash,
    textId: results?.textListType,
  });

  const riskResult = useReadingAssessmentRisk(testResults);

  const layoutError = useMemo(() => {
    if (!results) return new Error("Missing reading assessment results.");
    if (results.schemaVersion !== 2) {
      return new Error(
        `Unsupported reading assessment schemaVersion: ${String(results.schemaVersion)}`,
      );
    }
    return layout.error;
  }, [results, layout.error]);

  const layoutPages = layout.pages;
  const layoutManifest = layout.manifest;

  const raMetrics = (results?.metrics as ReadingAssessmentDerivedMetrics | undefined) ?? null;

  const fixationRecordsByPageIndex = useMemo(() => {
    const map: Record<number, ReadingFixationRecord[]> = {};
    if (!raMetrics?.pageMetrics?.length) return map;

    for (const page of raMetrics.pageMetrics) {
      if (Number.isFinite(page.pageIndex) && page.fixationRecords?.length) {
        map[page.pageIndex] = page.fixationRecords;
      }
    }
    return map;
  }, [raMetrics]);

  const [gazeMode, setGazeMode] = useState<"path" | "heatmap">("path");

  const wordHeatmapByPage = useMemo(() => {
    if (gazeMode !== "heatmap" || !layoutPages?.length) return undefined;
    const result: Record<number, Record<number, string>> = {};
    for (const page of layoutPages) {
      const fixations = fixationRecordsByPageIndex[page.index];
      if (!fixations?.length || !page.words?.length) continue;
      const gazeTime = aggregateGazeTimePerWord(fixations, page.words);
      const colors = gazeTimeToColorMap(gazeTime);
      if (Object.keys(colors).length > 0) {
        result[page.index] = colors;
      }
    }
    return Object.keys(result).length > 0 ? result : undefined;
  }, [gazeMode, layoutPages, fixationRecordsByPageIndex]);

  const { prettier } = usePrintableContext();

  if (!riskResult) {
    return (
      <Printable.Section>
        <Stack>
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <Card withBorder radius="md" p="xl">
              <Text c="dimmed">No reading assessment metrics available for this test.</Text>
            </Card>
          </Box>
        </Stack>
      </Printable.Section>
    );
  }

  const comprehensionTable = results?.comprehensionAnswers?.length ? (
    <Card withBorder>
      <Text fw={600} size="sm" mb="xs" c="dimmed">
        Comprehension Questions Details
      </Text>
      <Divider mb="md" />
      <Table striped highlightOnHover verticalSpacing="xs">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>#</Table.Th>
            <Table.Th>Question</Table.Th>
            <Table.Th>Chosen</Table.Th>
            <Table.Th>Correct</Table.Th>
            <Table.Th>Result</Table.Th>
            <Table.Th>Time (ms)</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {results.comprehensionAnswers.map((a) => (
            <Table.Tr key={a.questionIndex}>
              <Table.Td>{a.questionIndex + 1}</Table.Td>
              <Table.Td style={{ maxWidth: 300 }}>
                <Text size="sm" lineClamp={2}>
                  {a.questionText}
                </Text>
              </Table.Td>
              <Table.Td>{a.chosenAnswer}</Table.Td>
              <Table.Td>{a.correctAnswer}</Table.Td>
              <Table.Td>
                <Badge size="sm" color={a.isCorrect ? "green" : "red"}>
                  {a.isCorrect ? "Correct" : "Incorrect"}
                </Badge>
              </Table.Td>
              <Table.Td>{a.responseTimeMs}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  ) : null;

  const technicalDetailsContent = (
    <Box>
      <Group gap="xs" mt="lg" mb="md" align="center">
        <IconSettings size={18} />
        <Title order={4} c="gray.8" fw={700}>
          Technical Details & Gaze Analysis
        </Title>
      </Group>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Card
            withBorder
            style={{
              display: "flex",
              flexDirection: "column",
              minHeight: 500,
              overflow: "visible",
            }}
          >
            <Flex justify="space-between" align="center" mb="xs" wrap="wrap" gap="xs">
              <Text fw={600} size="sm" c="dimmed">
                Gaze Replay & Line Tracking
              </Text>
              <SegmentedControl
                size="sm"
                color="orange"
                radius="lg"
                className="ra-stable-segment-control"
                value={gazeMode}
                onChange={(v) => setGazeMode(v as "path" | "heatmap")}
                data={[
                  { label: "Gaze Path", value: "path" },
                  { label: "Word Heatmap", value: "heatmap" },
                ]}
                styles={{ label: { marginBottom: 0 } }}
              />
            </Flex>
            <Divider mb="md" />

            {layoutError ? (
              <Text c="red" size="sm">
                Failed to load reading layout:{" "}
                {layoutError instanceof Error ? layoutError.message : String(layoutError)}
              </Text>
            ) : null}

            {!layoutPages && layout.isLoading ? (
              <Text size="sm" c="dimmed">
                Loading reading layout...
              </Text>
            ) : layoutManifest?.layout && layoutPages?.length ? (
              <Box
                className="ra-hide-scrollbar"
                style={{ flex: 1, width: "100%", overflowX: "auto" }}
              >
                <ReadingLayoutRenderer
                  layout={layoutManifest.layout}
                  pages={layoutPages}
                  fixationRecordsByPageIndex={
                    gazeMode === "path" ? fixationRecordsByPageIndex : undefined
                  }
                  textOrientation={results?.textOrientation}
                  showEstimatedLines={gazeMode === "path"}
                  showFixationSlider={gazeMode === "path" && !prettier}
                  maxContentWidthPx={0}
                  contentWidthRatio={1}
                  wordBackgroundByIndex={wordHeatmapByPage}
                  renderPageHeader={(page) => (
                    <Text fw={600} c="dimmed" size="xs">
                      Paragraph {page.index + 1} — Fixations:{" "}
                      {fixationRecordsByPageIndex[page.index]?.length ?? 0}
                    </Text>
                  )}
                />
                {gazeMode === "heatmap" ? (
                  <Group justify="center" mt="md" gap="xs">
                    <Text size="xs" c="dimmed" fw={600}>
                      Gaze time:
                    </Text>
                    <Box
                      style={{
                        width: 120,
                        height: 10,
                        borderRadius: 5,
                        background:
                          "linear-gradient(to right, hsla(60,80%,92%,0.3), hsla(40,90%,75%,0.6), hsla(20,95%,65%,0.75), hsla(0,95%,57%,0.85))",
                      }}
                    />
                    <Flex gap={4}>
                      <Text size="10px" c="dimmed">
                        Short
                      </Text>
                      <Text size="10px" c="dimmed">
                        →
                      </Text>
                      <Text size="10px" c="dimmed">
                        Long
                      </Text>
                    </Flex>
                  </Group>
                ) : null}
              </Box>
            ) : (
              <Text size="sm" c="dimmed">
                No layout pages returned.
              </Text>
            )}
          </Card>
        </Grid.Col>
      </Grid>
    </Box>
  );

  const disclaimer = (
    <Text size="xs" c="dimmed" ta="center" px="md" py="sm">
      This assessment identifies reading patterns that may indicate risk for reading difficulties.
      It is not a clinical diagnosis. If results suggest concern, we recommend consulting a
      qualified educational psychologist or specialist.
    </Text>
  );

  return (
    <>
      <Printable.Section>
        <Stack gap="xl">
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <ReadingAssessmentDashboard risk={riskResult} />
          </Box>
        </Stack>
      </Printable.Section>
      <Printable.Section>
        <Box mx={prettier ? 0 : "xl"}>
          <Stack gap="xl">
            <ReadingAssessmentScoreSummary risk={riskResult} />
            {comprehensionTable}
          </Stack>
        </Box>
      </Printable.Section>
      <Printable.Section>
        <Box mx={prettier ? 0 : "xl"}>
          <Stack gap="xl">
            {technicalDetailsContent}
            {disclaimer}
          </Stack>
        </Box>
      </Printable.Section>
    </>
  );
}

ReadingAssessmentTestEntry.selfSections = true;

export default ReadingAssessmentTestEntry;
